'use client';

import { type CSSProperties, type ReactNode, useState } from 'react';
import Link from '~/(tenant)/_components/tenant-link';
import {
  AlertCircle,
  CalendarClock,
  ChevronRight,
  CreditCard,
  Hash,
  RefreshCw,
  Users,
} from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { formatLocaleDate } from '@kit/shared/utils';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import type { MyMembershipRow } from '../_lib/server/memberships-page.loader';
import { formatAmount, isComplimentaryMembership } from '../_lib/format';

const PAID_PAYMENT_STATES = new Set([
  'succeeded',
  'cash_paid',
  'card_paid',
  'online_paid',
  'bank_transfer_paid',
  // Wallet settlement writes no payment_transactions row by design, so
  // get_my_memberships reports outstanding = full price. Without this the
  // customer sees an amber "Outstanding · Pay" banner on the membership they
  // just paid from their wallet — while /payment/membership/<id> shows it as
  // confirmed. Matches the set already used by the /payment surfaces.
  'wallet_paid',
]);

const PERIOD_LABEL_KEYS: Record<string, string> = {
  monthly: 'common:memberClub.memberships.card.periodMonthly',
  quarterly: 'common:memberClub.memberships.card.periodQuarterly',
  semi_annual: 'common:memberClub.memberships.card.periodSemiAnnual',
  annual: 'common:memberClub.memberships.card.periodAnnual',
  lifetime: 'common:memberClub.memberships.card.periodLifetime',
};

const PERIOD_LABEL_DEFAULTS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-annual',
  annual: 'Annual',
  lifetime: 'Lifetime',
};

/**
 * Editorial membership row. Mirrors the bookings card aesthetic — left
 * rail with status month/expiry, display-font title, soft meta rows, and
 * a footer with pills + text links. Pay CTA renders as a full-width pill
 * when there's outstanding balance (mirrors the bookings Pay Now button).
 */
export function NativeMembershipCard({
  membership,
  showPlanPrice = true,
}: {
  membership: MyMembershipRow;
  /** The home screen hides the plan price; the period shows on the rail. */
  showPlanPrice?: boolean;
}) {
  const t = useNativeTokens();
  const { t: translate, i18n } = useTranslation();

  const isPaid = PAID_PAYMENT_STATES.has(membership.payment_status);
  const hasOutstanding = !isPaid && membership.outstanding > 0;

  const expiresAt = membership.expires_at
    ? new Date(membership.expires_at)
    : null;
  const isExpiringSoon = expiresAt
    ? (expiresAt.getTime() - Date.now()) / 86_400_000 <= 30 &&
      expiresAt.getTime() > Date.now()
    : false;
  const isExpired = membership.status === 'expired';
  const isCancelled = membership.status === 'cancelled';

  const isRenewable =
    membership.plan_period !== 'lifetime' &&
    (isExpired || isCancelled || isExpiringSoon);

  const periodKey = PERIOD_LABEL_KEYS[membership.plan_period];
  const periodDefault = PERIOD_LABEL_DEFAULTS[membership.plan_period];
  const periodLabel = periodKey
    ? translate(periodKey, { defaultValue: periodDefault ?? '' })
    : '';

  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const isActive = isHovered || isFocused;

  return (
    <article
      style={{
        position: 'relative',
        display: 'flex',
        gap: 18,
        padding: '22px 14px',
        marginInline: -14,
        borderRadius: t.radius,
        backgroundColor: isActive
          ? 'rgba(0, 0, 0, 0.025)'
          : 'transparent',
        opacity: isCancelled ? 0.6 : 1,
        cursor: 'pointer',
        transform: isPressed ? 'scale(0.997)' : 'none',
        transition:
          'background-color 200ms ease, transform 120ms ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false);
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      data-test="native-membership-card"
      data-membership-id={membership.id}
    >
      {/* Invisible overlay link — makes the whole card clickable.
          Inner interactive elements (Renew, Pay) are lifted above this
          with position: relative + zIndex: 1 so they keep their own
          destinations and aren't swallowed by the card link. */}
      <Link
        href={`/memberships/${membership.id}`}
        aria-label={translate('common:memberClub.memberships.card.viewAria', {
          planName: membership.plan_name,
          defaultValue: 'View {{planName}} membership',
        })}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: t.radius,
          outline: 'none',
          boxShadow: isFocused
            ? `0 0 0 2px ${t.background}, 0 0 0 4px ${t.foreground}`
            : 'none',
          transition: 'box-shadow 150ms ease',
        }}
        data-test="native-membership-card-link"
      />

      <PlanRail
        color={membership.plan_color ?? '#1C1C1C'}
        periodLabel={periodLabel}
        active={isActive}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontFamily: t.fontDisplay,
              fontSize: 22,
              fontWeight: 400,
              letterSpacing: '-0.01em',
              lineHeight: 1.15,
              color: t.foreground,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textDecorationLine: isActive ? 'underline' : 'none',
              textDecorationThickness: 1,
              textUnderlineOffset: 5,
              transition: 'text-decoration-color 150ms ease',
            }}
          >
            {membership.plan_name}
          </h3>
          <ChevronRight
            size={16}
            strokeWidth={1.4}
            color={t.muted}
            aria-hidden
            style={{
              flexShrink: 0,
              opacity: isActive ? 1 : 0.4,
              transform: isActive
                ? 'translateX(2px)'
                : 'translateX(0)',
              transition:
                'opacity 200ms ease, transform 200ms ease',
            }}
          />
        </div>

        {membership.is_child && membership.member_name ? (
          <p className="text-muted-foreground text-sm" data-test="membership-child-name">
            {membership.member_name}
          </p>
        ) : null}

        {showPlanPrice && (
        <p
          style={{
            margin: '4px 0 0',
            fontFamily: t.fontSans,
            fontSize: 13,
            color: t.muted,
            fontStyle: 'italic',
          }}
        >
          {isComplimentaryMembership(membership) ? (
            <Trans
              i18nKey="common:memberClub.memberships.card.complimentary"
              defaults="Complimentary"
            />
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
              <CurrencyIcon
                style={{ width: 10, height: 10, strokeWidth: 1.6, alignSelf: 'center' }}
              />
              {formatAmount(membership.plan_price)}
            </span>
          )}
          {periodLabel ? ` · ${periodLabel.toLowerCase()}` : ''}
        </p>
        )}

        <ul
          style={{
            listStyle: 'none',
            margin: '14px 0 0',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {membership.member_number ? (
            <MetaRow icon={<Hash size={14} strokeWidth={1.5} />}>
              <span style={{ color: t.foreground, fontFamily: t.fontMono, fontSize: 12 }}>
                {membership.member_number}
              </span>
            </MetaRow>
          ) : null}

          {expiresAt ? (
            <MetaRow icon={<CalendarClock size={14} strokeWidth={1.5} />}>
              <span style={{ color: t.foreground }}>
                <Trans
                  i18nKey={
                    isExpired
                      ? 'common:memberClub.memberships.card.expiredPrefix'
                      : 'common:memberClub.memberships.card.expiresPrefix'
                  }
                  values={{
                    date: formatLocaleDate(expiresAt, i18n.language, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    }),
                  }}
                  defaults={
                    isExpired ? 'Expired {{date}}' : 'Expires {{date}}'
                  }
                />
              </span>
            </MetaRow>
          ) : null}

          {membership.visits_count > 0 || membership.guest_passes_count > 0 ? (
            <MetaRow icon={<Users size={14} strokeWidth={1.5} />}>
              <span style={{ color: t.muted }}>
                {translate(
                  'common:memberClub.memberships.card.visitCount',
                  {
                    count: membership.visits_count,
                    defaultValue:
                      membership.visits_count === 1
                        ? '{{count}} visit'
                        : '{{count}} visits',
                  },
                )}
                {' · '}
                {translate(
                  'common:memberClub.memberships.card.guestCount',
                  {
                    count: membership.guest_passes_count,
                    defaultValue:
                      membership.guest_passes_count === 1
                        ? '{{count}} guest invited'
                        : '{{count}} guests invited',
                  },
                )}
              </span>
            </MetaRow>
          ) : null}
        </ul>

        <footer
          style={{
            marginTop: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <StatusPill status={membership.status} />

          <If condition={hasOutstanding}>
            <OutstandingPill amount={membership.outstanding} />
          </If>

          <If condition={isRenewable}>
            <div
              style={{
                marginInlineStart: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <TextLink href={`/memberships/${membership.id}?renew=1`} underline>
                <Trans i18nKey="common:renew" defaults="Renew" />
              </TextLink>
            </div>
          </If>
        </footer>

        <If condition={hasOutstanding}>
          <Link
            href={`/payment/membership/${membership.id}`}
            style={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              marginTop: 14,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '12px 18px',
              backgroundColor: t.accent,
              color: t.background,
              border: 'none',
              borderRadius: t.radius,
              fontFamily: t.fontSans,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              textDecoration: 'none',
            }}
            data-test="native-membership-pay"
          >
            <CreditCard size={13} strokeWidth={1.6} />
            <Trans i18nKey="common:payNow" defaults="Pay" />
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
              <CurrencyIcon
                style={{ width: 11, height: 11, strokeWidth: 1.6, alignSelf: 'center' }}
              />
              {formatAmount(membership.outstanding)}
            </span>
          </Link>
        </If>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

function PlanRail({
  color,
  periodLabel,
  active = false,
}: {
  color: string;
  periodLabel: string;
  active?: boolean;
}) {
  const t = useNativeTokens();
  return (
    <div
      style={{
        width: 52,
        textAlign: 'center',
        flexShrink: 0,
        paddingTop: 4,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          backgroundColor: color,
          opacity: active ? 1 : 0.85,
          margin: '0 auto',
          transform: active ? 'scale(1.06)' : 'scale(1)',
          transition: 'opacity 200ms ease, transform 200ms ease',
        }}
      />
      {periodLabel ? (
        <div
          style={{
            fontFamily: t.fontMono,
            fontSize: 9,
            letterSpacing: '0.22em',
            color: t.muted,
            marginTop: 8,
            textTransform: 'uppercase',
          }}
        >
          {periodLabel.split(' ')[0]}
        </div>
      ) : null}
    </div>
  );
}

function MetaRow({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  const t = useNativeTokens();
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: t.fontSans,
        fontSize: 13,
        color: t.muted,
        lineHeight: 1.4,
      }}
    >
      <span style={{ display: 'inline-flex', color: t.muted, flexShrink: 0 }}>
        {icon}
      </span>
      <span style={{ minWidth: 0 }}>{children}</span>
    </li>
  );
}

function TextLink({
  children,
  href,
  underline = false,
}: {
  children: ReactNode;
  href: string;
  underline?: boolean;
}) {
  const t = useNativeTokens();
  return (
    <Link
      href={href}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        color: t.foreground,
        fontFamily: t.fontSans,
        fontSize: 13,
        fontWeight: 500,
        textDecoration: underline ? 'underline' : 'none',
        textUnderlineOffset: 4,
      }}
    >
      {children}
    </Link>
  );
}

type PillTone = 'green' | 'amber' | 'red' | 'neutral';

const PILL_TONE_STYLES: Record<PillTone, { bg: string; fg: string; dot: string }> = {
  green: { bg: '#E5F1E8', fg: '#2F6B3A', dot: '#3F8F4F' },
  amber: { bg: '#F8EFD7', fg: '#7A5616', dot: '#B68628' },
  red: { bg: '#F4E1DD', fg: '#86372C', dot: '#B25040' },
  neutral: { bg: 'rgba(0,0,0,0.05)', fg: 'inherit', dot: 'currentColor' },
};

function StatusPill({ status }: { status: string }) {
  const t = useNativeTokens();
  const { t: translate } = useTranslation();

  const { tone, label } = (() => {
    switch (status) {
      case 'active':
        return {
          tone: 'green' as PillTone,
          label: translate('common:memberClub.memberships.status.active', {
            defaultValue: 'active',
          }),
        };
      case 'pending':
        return {
          tone: 'amber' as PillTone,
          label: translate('common:memberClub.memberships.status.pending', {
            defaultValue: 'pending',
          }),
        };
      case 'approved':
        // Approved by admin, customer hasn't paid yet. Member-side reads this
        // as "awaiting payment" — slightly more urgent visual than 'pending'.
        return {
          tone: 'amber' as PillTone,
          label: translate('common:memberClub.memberships.status.approved', {
            defaultValue: 'awaiting payment',
          }),
        };
      case 'on_hold':
        return {
          tone: 'amber' as PillTone,
          label: translate('common:memberClub.memberships.status.onHold', {
            defaultValue: 'on hold',
          }),
        };
      case 'suspended':
        return {
          tone: 'red' as PillTone,
          label: translate(
            'common:memberClub.memberships.status.suspended',
            { defaultValue: 'suspended' },
          ),
        };
      case 'expired':
        return {
          tone: 'red' as PillTone,
          label: translate('common:memberClub.memberships.status.expired', {
            defaultValue: 'expired',
          }),
        };
      case 'cancelled':
        return {
          tone: 'neutral' as PillTone,
          label: translate(
            'common:memberClub.memberships.status.cancelled',
            { defaultValue: 'cancelled' },
          ),
        };
      default:
        return { tone: 'neutral' as PillTone, label: status };
    }
  })();

  const palette = PILL_TONE_STYLES[tone];
  const fg = palette.fg === 'inherit' ? t.foreground : palette.fg;

  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px',
    borderRadius: 999,
    backgroundColor: palette.bg,
    color: fg,
    fontFamily: t.fontMono,
    fontSize: 10,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    fontWeight: 500,
  };

  return (
    <span style={style} data-test="native-membership-status">
      {tone === 'neutral' ? (
        <AlertCircle size={10} strokeWidth={1.8} />
      ) : (
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            backgroundColor: palette.dot,
          }}
        />
      )}
      {label}
    </span>
  );
}

function OutstandingPill({ amount }: { amount: number }) {
  const t = useNativeTokens();
  const palette = PILL_TONE_STYLES.amber;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 12px',
        borderRadius: 999,
        backgroundColor: palette.bg,
        color: palette.fg,
        fontFamily: t.fontMono,
        fontSize: 10,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        fontWeight: 500,
      }}
      data-test="native-membership-outstanding"
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          backgroundColor: palette.dot,
        }}
      />
      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3 }}>
        <CurrencyIcon
          style={{ width: 9, height: 9, strokeWidth: 1.8, alignSelf: 'center' }}
        />
        {formatAmount(amount)}{' '}
        <Trans
          i18nKey="common:memberClub.memberships.card.outstandingDueSuffix"
          defaults="due"
        />
      </span>
    </span>
  );
}
